import { useEffect, useState } from "react";

import { fetchAddressEGrid } from "../utils/apiCalls";

const UNRESOLVED_EGRID = "world";

export const isSpecificEGrid = (eGrid) =>
  !!eGrid && eGrid !== UNRESOLVED_EGRID;

const resolveSourceEGrid = async ({ storedEGrids = [], address }, fetchEGrid) => {
  const storedEGrid = storedEGrids.find(isSpecificEGrid);
  if (storedEGrid) {
    return storedEGrid;
  }

  if (!address) {
    return undefined;
  }

  const derivedEGrid = await fetchEGrid(address).catch(() => undefined);
  return isSpecificEGrid(derivedEGrid) ? derivedEGrid : undefined;
};

//Mirrors the backend's resolveUtilityEGrid (functions/utils/calcs/utilitiesCalcs.js):
//sources in priority order, each one's stored eGrid first, else one derived from
//its address. Without the address step, an office with nothing stored was charged
//eGRID MROE, the highest-intensity US subregion (a Vancouver office ran ~45x high).
//A derived "world" means "couldn't resolve" and is discarded, so the calc keeps its
//conservative MROE default; a stored "world" only wins when nothing better exists.
//keepStoredUnresolved: a stored "world" is only a safe last resort where the
//form always priced "world" at the global average (electricity). Chill-water's
//old /egrid-tons-per-mwh lookup mapped it to MROE, so it passes false to keep
//the conservative default instead of dropping to the global average.
export const resolveUtilityEGrid = async (
  sources = [],
  fetchEGrid = fetchAddressEGrid,
  { keepStoredUnresolved = true } = {}
) => {
  const specificEGrid = await sources.reduce(
    async (accPromise, source) =>
      (await accPromise) || resolveSourceEGrid(source, fetchEGrid),
    Promise.resolve(undefined)
  );

  if (specificEGrid || !keepStoredUnresolved) {
    return specificEGrid;
  }
  return sources.flatMap(({ storedEGrids = [] }) => storedEGrids).find(Boolean);
};

//Only the first (most specific) source's stored eGrid skips the lookup: a
//stored company eGrid must not beat an office address that resolves on its own.
const findImmediateEGrid = ([firstSource] = []) =>
  (firstSource?.storedEGrids || []).find(isSpecificEGrid);

export const useResolvedUtilityEGrid = (
  sources,
  { keepStoredUnresolved = true } = {}
) => {
  const sourcesKey = JSON.stringify({ sources, keepStoredUnresolved });
  const immediateEGrid = findImmediateEGrid(sources);
  const [resolved, setResolved] = useState({ key: null, eGrid: undefined });

  useEffect(() => {
    if (immediateEGrid) {
      return undefined;
    }

    let isCurrent = true;
    const { sources: parsedSources, keepStoredUnresolved: keepStored } =
      JSON.parse(sourcesKey);
    resolveUtilityEGrid(parsedSources, fetchAddressEGrid, {
      keepStoredUnresolved: keepStored
    }).then((eGrid) => {
      if (isCurrent) {
        setResolved({ key: sourcesKey, eGrid });
      }
    });

    return () => {
      isCurrent = false;
    };
  }, [sourcesKey, immediateEGrid]);

  if (immediateEGrid) {
    return { eGrid: immediateEGrid, eGridLoading: false };
  }

  const isResolvedForSources = resolved.key === sourcesKey;
  return {
    eGrid: isResolvedForSources ? resolved.eGrid : undefined,
    eGridLoading: !isResolvedForSources
  };
};
